import { updateUserSuccess } from '@features/users/actions/update-user.actions';
import { Action, createReducer, createFeatureSelector, createSelector, on } from '@ngrx/store';
import { endSession, endSessionFailure, expireSessionAndLogout } from '@shared/actions/end-session.actions';
import { loadSession, loadSessionFailure, loadSessionSuccess } from '@shared/actions/load-session.actions';
import { toggleMenu } from '@shared/actions/toggle-menu.actions';
import { Config } from '@shared/models/config';
import { Session } from '@shared/models/session';
import { LoadingState } from '@shared/types/loading-state';
import { disableMfaSuccess } from '@features/mfa/actions/disable-mfa.actions';
import { generateMfaRecoveryCodesSuccess } from '@features/mfa/actions/generate-mfa-recovery-codes.actions';
import moment from 'moment';
import { sendAgreementForSignaturesSuccess } from '@shared/actions/send-agreement-for-signatures.actions';
import { updatePartnerDetailsSuccess } from '@features/customers/partner-details/actions/update-partner-details.actions';
import { environment } from '@environments/environment';
import { Group } from '@shared/models/group';

export const featureKey = 'session';

export interface State {
  loadingState: LoadingState;
  loadingText: string | undefined;
  session: Session | undefined;
  config: Config | undefined;
  menuOpen: boolean;
}

export const initialState: State = {
  loadingState: 'not_loading',
  loadingText: undefined,
  session: undefined,
  config: undefined,
  menuOpen: false,
};

export const sessionReducer = createReducer(
  initialState,
  on(loadSession, expireSessionAndLogout, endSession, (state, {}) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
  })),
  on(loadSessionSuccess, (state, { session, config }) => ({
    ...state,
    loadingState: 'loaded' as LoadingState,
    session: session,
    config: config,
  })),
  on(endSession, (state, {}) => ({
    ...state,
    loadingText: 'Logging out...',
  })),
  on(loadSessionFailure, endSessionFailure, (state, {}) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
  })),
  on(updateUserSuccess, (state, { user, currentUserEdit }) => {
    if (currentUserEdit) {
      return {
        ...state,
        session: {
          ...state.session!,
          email: user.attributes.email,
          pending_email: user.attributes.pending_email,
          email_confirmed: user.attributes.email_confirmed,
          full_name: user.attributes.full_name,
          mfa_setting: user.attributes.mfa_setting,
        },
      };
    }
    return state;
  }),
  on(disableMfaSuccess, (state, { userId }) => {
    if (userId === state.session!.id && state.session!.mfa_setting!.enforced) {
      return {
        ...state,
        session: {
          ...state.session!,
          mfa_setting: { ...state.session!.mfa_setting!, active: false },
        },
      };
    }
    return state;
  }),
  on(generateMfaRecoveryCodesSuccess, (state, { userId }) => {
    if (state.session!.mfa_setting!.enforced) {
      return {
        ...state,
        session: {
          ...state.session!,
          mfa_setting: { ...state.session!.mfa_setting!, active: true },
        },
      };
    }
    return state;
  }),
  on(toggleMenu, (state) => ({
    ...state,
    menuOpen: !state.menuOpen,
  })),
  on(sendAgreementForSignaturesSuccess, (state, { groupId }) => {
    if (state.session && state.session.groups) {
      const updatedGroups = state.session.groups.map((group) =>
        group.id === groupId
          ? { ...group, groups_agreement: { ...group.groups_agreement, external_id: 'external_id' } }
          : group,
      );
      return {
        ...state,
        session: { ...state.session, groups: updatedGroups },
      };
    }
    return state;
  }),
  on(updatePartnerDetailsSuccess, (state, { group }) => {
    if (state.session) {
      const updateSessionGroup = state.session.groups.map((g) =>
        g.id === group.id ? { ...g, ...group, last_confirmed_at: group.attributes.last_confirmed_at } : g,
      );
      return {
        ...state,
        session: { ...state.session, groups: updateSessionGroup },
      };
    }
    return state;
  }),
);

export function reducer(state: State | undefined, action: Action) {
  return sessionReducer(state, action);
}

export const sessionFeatureSelector = createFeatureSelector<State>(featureKey);

export const selectSessionLoadingState = createSelector(sessionFeatureSelector, (state) => state.loadingState);

export const selectSessionLoadingText = createSelector(sessionFeatureSelector, (state) => state.loadingText);

export const selectSession = createSelector(sessionFeatureSelector, (state) => state.session);

export const selectGroups = createSelector(sessionFeatureSelector, (state) => state.session?.groups);

export const selectTradingEntityGroup = createSelector(selectGroups, (groups) => {
  return (groups as Partial<Group['attributes']>[])?.find((group) =>
    group.trading_entity?.includes(environment.trading_entity),
  );
});

export const selectTradingEntityGroupId = createSelector(selectTradingEntityGroup, (group) => {
  return group?.id;
});

export const selectUserType = createSelector(sessionFeatureSelector, (state) => state.session?.user_type);

export const selectSessionPermissions = createSelector(sessionFeatureSelector, (state) => state.session?.permissions);

export const selectConfig = createSelector(sessionFeatureSelector, (state) => state.config);

export const selectMenuOpen = createSelector(sessionFeatureSelector, (state) => state.menuOpen);

export const selectLimitedAccessDaysLeft = createSelector(sessionFeatureSelector, (state) => {
  if (!state.session || !state.config) return undefined;
  const limitedAccessDays = state.config.limits.groups_agreement_signing_limited_access_in_days;
  if (!limitedAccessDays) return undefined;

  const limitedAccessStartedAt = moment(state.session.groups[0].groups_agreement?.limited_access_started_at);
  if (!limitedAccessStartedAt.isValid()) return undefined;

  const daysSinceStart = moment().diff(limitedAccessStartedAt, 'days');
  return limitedAccessDays - daysSinceStart < 0 ? 0 : limitedAccessDays - daysSinceStart;
});

export const selectLimitedAccessAllowed = createSelector(
  sessionFeatureSelector,
  selectLimitedAccessDaysLeft,
  (state, daysLeft) => {
    return state.session?.groups[0].groups_agreement?.limited_access_allowed && !!daysLeft && daysLeft >= 0;
  },
);

export const selectAccessRestrictedWithoutAgreement = createSelector(
  sessionFeatureSelector,
  selectLimitedAccessAllowed,
  (state, limitedAccessAllowed) => {
    return (
      state.session?.user_type != 'intranet' &&
      !state?.session?.groups[0].groups_agreement?.signed_at &&
      !limitedAccessAllowed
    );
  },
);
